import { useEffect, useState } from "react";
import { Plus, Loader2, X, Trash2, Star } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "border border-brand-line rounded-xl px-4 py-2.5 w-full focus:ring-2 focus:ring-brand-accent focus:border-brand-accent outline-none bg-white text-brand-ink";

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type={onChange ? "button" : undefined} onClick={onChange ? () => onChange(n) : undefined} disabled={!onChange}
          data-testid={onChange ? `star-${n}` : undefined}>
          <Star className={`w-5 h-5 ${n <= value ? "fill-yellow-400 text-yellow-400" : "text-brand-line"}`} />
        </button>
      ))}
    </div>
  );
}

export default function Reviews() {
  const [reviews, setReviews] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/reviews").then(({ data }) => setReviews(data));
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Supprimer l'avis de ${r.name} ?`)) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Avis supprimé"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (reviews === null) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-brand" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-brand-ink">Avis clients</h1>
          <p className="text-brand-muted mt-1">Consultez, ajoutez ou supprimez les avis affichés sur les fiches produit.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="add-review-button" className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2.5 font-semibold flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Ajouter un avis
        </button>
      </div>

      {reviews.length === 0 ? (
        <div className="bg-white border border-brand-line rounded-2xl p-10 text-center text-brand-muted">
          Aucun avis pour le moment. Ajoutez-en un pour rassurer vos clients.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-card-${r.id}`} className="bg-white border border-brand-line rounded-2xl p-5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-heading font-semibold text-brand-ink">{r.name}</p>
                  <p className="text-xs text-brand-accent font-semibold">{r.product_name}</p>
                </div>
                <button onClick={() => remove(r)} data-testid={`delete-review-${r.id}`} className="p-2 text-brand-secondary hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
              <div className="mt-2"><Stars value={r.rating} /></div>
              {r.comment && <p className="text-sm text-brand-sub mt-2">{r.comment}</p>}
              <p className="text-xs text-brand-muted mt-3">{new Date(r.created_at).toLocaleDateString("fr-FR")}</p>
            </div>
          ))}
        </div>
      )}

      {creating && <NewReviewModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function NewReviewModal({ onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", name: "", rating: 5, comment: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data);
      if (data[0]) setForm((f) => ({ ...f, product_id: data[0].id }));
    });
  }, []);

  const save = async () => {
    if (!form.product_id || !form.name) { setError("Produit et nom requis."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/admin/reviews", form);
      toast.success("Avis ajouté");
      onSaved();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Ajouter un avis</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-brand-cream rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <select data-testid="review-product" className={inputCls} value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })}>
            {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input data-testid="review-name" className={inputCls} placeholder="Nom du client" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div>
            <label className="text-xs text-brand-muted">Note</label>
            <div className="mt-1"><Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} /></div>
          </div>
          <textarea className={inputCls} rows={3} placeholder="Commentaire (optionnel)" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="review-comment" />
          {error && <p className="text-sm text-brand-secondary">{error}</p>}
          <button onClick={save} disabled={loading} data-testid="save-review-button" className="w-full bg-brand text-white hover:bg-brand-hover rounded-full px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Publier l'avis"}
          </button>
        </div>
      </div>
    </div>
  );
}
